// The Close Deal profit breakdown: the "Your profit" field and the popover it
// opens. The field is the sale's total and nothing else — it has no value of its
// own, so there is only ever one profit number on the form.
//
// Trade-in and Broker Fee are fixed rows that map to the Opportunity columns of
// the same name. Everything after them is a named custom row; the modal owns the
// array and this component only edits it.
import React, { useMemo, useRef, useState } from 'react'
import Popover from '@mui/material/Popover'
import CloseIcon from '@mui/icons-material/Close'
import { keepEscapeInOpenSelect } from '../selectEscape'
import './ProfitBreakdown.scss'

// Same convention as MarkSoldModal's own parseMoney: everything but digits and a
// decimal point is dropped, so "$1,100" reads as 1100 and a typed "-" cannot
// make a negative — the form has never accepted one.
export const parseMoney = (s) => Number(String(s || '').replace(/[^0-9.]/g, '')) || 0

// Summed in whole cents. Adding dollars as floats drifts (0.1 + 0.2), and this
// total is what gets written to the deal.
export const rowsTotal = (rows) =>
    rows.reduce((cents, r) => cents + Math.round(parseMoney(r.amount) * 100), 0) / 100

// Only a saved deal's total can be negative (see signedRowsTotal).
const fmtMoney = (n) => `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`

export const TRADE_IN_KEY = 'trade_in'
export const BROKER_FEE_KEY = 'broker_fee'

// A row added to a saved deal's grid (Lead History) starts Paid. Close Deal
// starts every row Unpaid instead: a sale just closed has collected nothing yet.
const DEFAULT_STATUS = 'PAID'
const CLOSE_DEAL_STATUS = 'UNPAID'

let rowSeq = 0
export const blankRow = (status = DEFAULT_STATUS) => ({
    key: `custom-${++rowSeq}`, name: '', amount: '', status, fixed: false,
})

// A fixed row is never "blank" — it is always the anchor the next blank sits under.
const isBlank = (row) => !row.fixed && !row.name.trim() && !row.amount.trim()

// One blank row waits at the end at all times, so a rep never hunts for an "add"
// button. Rows are only ever appended here: a row the user emptied mid-list stays
// put (they may still be typing) and leaves by the remove control instead.
export const withTrailingBlank = (rows, status) => {
    const last = rows[rows.length - 1]
    return last && isBlank(last) ? rows : [...rows, blankRow(status)]
}

export const initialProfitRows = (brokerFeeAmount = '') => withTrailingBlank([
    { key: TRADE_IN_KEY, name: 'Trade-in', amount: '', status: CLOSE_DEAL_STATUS, fixed: true },
    { key: BROKER_FEE_KEY, name: 'Broker Fee', amount: brokerFeeAmount, status: CLOSE_DEAL_STATUS, fixed: true },
], CLOSE_DEAL_STATUS)

// What actually gets sent. A row with no money in it is not a fee, whatever it is
// called — but a row with money and no name still is, so it goes up unnamed
// rather than being thrown away.
export const rowsToItems = (rows) => rows
    .filter((r) => !r.fixed && parseMoney(r.amount) > 0)
    .map((r) => ({
        name: r.name.trim(),
        amount: parseMoney(r.amount).toFixed(2),
        status: r.status,
    }))

// Rebuilds the grid from a saved Opportunity, for surfaces that show a closed
// deal's profit rather than record one. Mirrors the backend's own profit-item
// rule (Opportunity._profit_items): custom rows replace the legacy other_profit
// bucket rather than joining it — the bucket is their sum, so showing both would
// double the total — and a zero amount is nothing to collect, so it is not a row
// at all. Staying in step is what keeps the grid agreeing with the collected
// count shown beside it.
export const itemsToRows = (opp) => {
    if (!opp) return []
    const rows = [
        { key: TRADE_IN_KEY, name: 'Trade-in', amount: opp.trade_in_profit, status: opp.trade_in_profit_status, fixed: true },
        { key: BROKER_FEE_KEY, name: 'Broker Fee', amount: opp.broker_fee_profit, status: opp.broker_fee_profit_status, fixed: true },
    ]
    if (opp.other_profit_items?.length) {
        rows.push(...opp.other_profit_items.map((item, i) => ({
            key: `saved-${i}`, name: item.name?.trim() || 'Custom fee',
            amount: item.amount, status: item.status, fixed: false,
        })))
    } else {
        // Imported and pre-breakdown records: one bucket carrying one status.
        rows.push({
            key: 'other', name: 'Other Profit',
            amount: opp.other_profit, status: opp.other_profit_status, fixed: true,
        })
    }
    return rows.filter((r) => parseMoney(r.amount) !== 0)
}

// ── Editing a saved deal ─────────────────────────────────────────────────────
//
// parseMoney drops a minus sign — right for Close Deal, which has never taken a
// negative, but it would flip a saved one on its way back. These keep the sign:
// a "-" before the first digit makes the amount negative.
export const parseSignedMoney = (s) => {
    const text = String(s ?? '')
    const n = parseMoney(text)
    return /^[^0-9]*-/.test(text) ? -n : n
}

export const signedRowsTotal = (rows) =>
    rows.reduce((cents, r) => cents + Math.round(parseSignedMoney(r.amount) * 100), 0) / 100

// Two decimals, as the backend requires; never "-0.00".
export const toAmount = (s) => {
    const fixed = parseSignedMoney(s).toFixed(2)
    return fixed === '-0.00' ? '0.00' : fixed
}

// A $0 amount loads blank, so the field shows its "$0" placeholder.
const editAmount = (v) => (parseSignedMoney(v) ? String(v) : '')

// The editable grid for a saved Opportunity. Unlike itemsToRows it drops
// nothing: both fixed rows stay at $0 (they are the only place to enter one),
// names stay as stored, and a legacy lump sum becomes a custom "Other Profit"
// row — sent back as a custom row, which keeps other_profit instead of zeroing it.
export const editableProfitRows = (opp) => {
    const rows = [
        { key: TRADE_IN_KEY, name: 'Trade-in', amount: editAmount(opp.trade_in_profit), status: opp.trade_in_profit_status || 'UNPAID', fixed: true },
        { key: BROKER_FEE_KEY, name: 'Broker Fee', amount: editAmount(opp.broker_fee_profit), status: opp.broker_fee_profit_status || 'UNPAID', fixed: true },
    ]
    if (opp.other_profit_items?.length) {
        rows.push(...opp.other_profit_items.map((item, i) => ({
            key: `saved-${i}`, name: item.name ?? '',
            amount: editAmount(item.amount), status: item.status || 'UNPAID', fixed: false,
        })))
    } else if (parseSignedMoney(opp.other_profit)) {
        rows.push({
            key: 'legacy-other', name: 'Other Profit',
            amount: String(opp.other_profit), status: opp.other_profit_status || 'UNPAID', fixed: false,
        })
    }
    return withTrailingBlank(rows)
}

// The same profit keys Close Deal sends (MarkSoldModal.profitPayload), signs
// kept. other_profit is not sent: the backend recomputes it from the items.
export const rowsToProfitPayload = (rows) => {
    const fixed = (key) => rows.find((r) => r.key === key) || { amount: '', status: 'UNPAID' }
    const tradeIn = fixed(TRADE_IN_KEY)
    const brokerFee = fixed(BROKER_FEE_KEY)
    return {
        trade_in_profit: toAmount(tradeIn.amount),
        trade_in_profit_status: tradeIn.status,
        broker_fee_profit: toAmount(brokerFee.amount),
        broker_fee_profit_status: brokerFee.status,
        other_profit_items: rows
            .filter((r) => !r.fixed && parseSignedMoney(r.amount) !== 0)
            .map((r) => ({ name: r.name.trim(), amount: toAmount(r.amount), status: r.status })),
    }
}

const STATUS_OPTIONS = [['PAID', 'Paid'], ['UNPAID', 'Unpaid']]

// Status is a Paid/Unpaid dropdown, green or red by value. A read-only grid shows
// the same control disabled, holding just its current value. The values stay
// PAID / UNPAID.
const StatusControl = ({ row, readOnly, onChange }) => {
    const value = row.status === 'PAID' ? 'PAID' : 'UNPAID'
    const options = readOnly ? STATUS_OPTIONS.filter(([v]) => v === value) : STATUS_OPTIONS
    return (
        <select
            className={`pb-status ${value === 'PAID' ? 'is-paid' : 'is-unpaid'}`}
            value={value}
            disabled={readOnly}
            aria-label={`${row.name.trim() || 'this fee'} status`}
            onChange={(e) => onChange(e.target.value)}
        >
            {options.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
        </select>
    )
}

const ProfitRow = ({ row, readOnly, canRemove, onPatch, onRemove }) => (
    <div className="pb-row">
        {row.fixed || readOnly
            ? <div className={`pb-type ${row.fixed ? 'is-fixed' : 'is-static'}`}>{row.name}</div>
            : <input
                className="pb-type"
                value={row.name}
                maxLength={60}
                placeholder="Custom fee name"
                aria-label="Custom fee name"
                onChange={(e) => onPatch({ name: e.target.value })}
            />}
        {readOnly
            ? <div className="pb-amount is-static">{fmtMoney(parseMoney(row.amount))}</div>
            : <input
                className="pb-amount"
                value={row.amount}
                inputMode="decimal"
                placeholder="$0"
                aria-label={`${row.name.trim() || 'Custom fee'} amount`}
                onChange={(e) => onPatch({ amount: e.target.value })}
            />}
        <StatusControl row={row} readOnly={readOnly} onChange={(status) => onPatch({ status })} />
        {/* A read-only grid has no remove column at all. */}
        {!readOnly && (canRemove
            ? <button
                type="button"
                className="pb-remove"
                aria-label={`Remove ${row.name.trim() || 'custom fee'}`}
                onClick={onRemove}
            ><CloseIcon /></button>
            : <span className="pb-remove is-spacer" aria-hidden="true" />)}
    </div>
)

// The grid itself — header, rows, total. Split out so a read-only surface gets
// the same presentation without the editing form around it. `total` lets a
// caller that keeps signs supply its own. Every profit popover renders this grid,
// so the status selects' Escape is kept here rather than in each popover.
export const ProfitGrid = ({ rows, readOnly = false, onPatch, onRemove, total }) => {
    const lastKey = rows[rows.length - 1]?.key
    return (
        <div className={`pb-grid${readOnly ? ' is-readonly' : ''}`} onKeyDown={keepEscapeInOpenSelect}>
            <div className="pb-head">
                <span>Type</span><span>Amount</span><span>Status</span>
                {!readOnly && <span aria-hidden="true" />}
            </div>
            <div className="pb-rows">
                {rows.map((row) => (
                    <ProfitRow
                        key={row.key}
                        row={row}
                        readOnly={readOnly}
                        // The waiting blank row has nothing to remove yet.
                        canRemove={!readOnly && !row.fixed && row.key !== lastKey}
                        onPatch={(patch) => onPatch(row.key, patch)}
                        onRemove={() => onRemove(row.key)}
                    />
                ))}
            </div>
            <div className={`pb-total${readOnly ? '' : ' is-editing'}`}>
                <span>Total</span>
                <span className="pb-total-amount">{fmtMoney(total ?? rowsTotal(rows))}</span>
            </div>
        </div>
    )
}

// `saved` edits a closed deal's rows (Edit Deal): signs kept, and a new row
// starts Paid like the Lead History grid's.
export const ProfitBreakdownField = ({ rows, onRowsChange, saved = false }) => {
    const [anchorEl, setAnchorEl] = useState(null)
    const anchorRef = useRef(null)
    const total = useMemo(() => (saved ? signedRowsTotal(rows) : rowsTotal(rows)), [rows, saved])
    const newRowStatus = saved ? DEFAULT_STATUS : CLOSE_DEAL_STATUS

    // Rows are replaced, never mutated, so a re-render can't miss an edit. The
    // trailing blank is topped up on every change, which is what makes typing a
    // name open the next row.
    const patchRow = (key, patch) => onRowsChange(
        withTrailingBlank(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)), newRowStatus)
    )
    const removeRow = (key) => onRowsChange(
        withTrailingBlank(rows.filter((r) => r.key !== key), newRowStatus)
    )

    const openPopover = () => setAnchorEl(anchorRef.current)
    // No onFocus: MUI hands focus back to the anchor when the popover closes, and
    // reopening on that would make Escape and click-away impossible.
    const onKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            openPopover()
        }
    }

    return (
        <div className="mfield">
            <div className="mfield-label">Your profit</div>
            <input
                ref={anchorRef}
                className="profit-total-input"
                value={fmtMoney(total)}
                readOnly
                aria-label="Your profit"
                aria-haspopup="dialog"
                onClick={openPopover}
                onKeyDown={onKeyDown}
            />
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ className: 'profit-breakdown-popover' }}
            >
                {/* Portaled out of .sold-modal, so these styles hang off this
                    root class instead of the modal's. */}
                <ProfitGrid rows={rows} onPatch={patchRow} onRemove={removeRow} total={total} />
            </Popover>
        </div>
    )
}
